import { supabase } from '../../utils/supabase'
import { HttpClient } from '../api/client'
import { TokenManager } from '../auth/tokenManager'
import { v4 as uuidv4 } from 'uuid'
import type {
  DocumentSummaryType,
  SurveySummaryStructured,
  InterviewSummaryStructured,
} from '@bandos/shared'

export interface VentureDocument {
  id: string
  file_name: string
  file_size: number
  mime_type: string
  status: 'processing' | 'ready' | 'error'
  summary: string | null
  content_classification: string | null
  error_message: string | null
  created_at: string
  ai_document_type: DocumentSummaryType | null
  ai_summary_structured: SurveySummaryStructured | InterviewSummaryStructured[] | null
  ai_summary_generated_at: string | null
}

const MAX_FILE_SIZE = 50 * 1024 * 1024 // 50MB
const ALLOWED_EXTENSIONS = ['.pdf', '.docx', '.txt']

function getFileExtension(fileName: string): string {
  const lastDot = fileName.lastIndexOf('.')
  return lastDot >= 0 ? fileName.slice(lastDot).toLowerCase() : ''
}

function sanitizeStorageFileName(fileName: string): string {
  return fileName.replace(/[^a-zA-Z0-9._-]/g, '_')
}

function getMimeType(fileName: string): string {
  const ext = getFileExtension(fileName)
  switch (ext) {
    case '.pdf':
      return 'application/pdf'
    case '.docx':
      return 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
    case '.txt':
      return 'text/plain'
    default:
      return ''
  }
}

export class DocumentService {
  static readonly MAX_FILE_SIZE = MAX_FILE_SIZE
  static readonly ALLOWED_EXTENSIONS = ALLOWED_EXTENSIONS

  /**
   * Upload a file to Supabase storage, then trigger backend processing.
   */
  static async uploadDocument(
    file: File,
    ventureNodeId: string,
    mindmapId: string,
    userId: string,
    contentClassification?: string
  ): Promise<{ documentId: string }> {
    // Validate extension
    const ext = getFileExtension(file.name)
    if (!ALLOWED_EXTENSIONS.includes(ext)) {
      throw new Error(`Unsupported file type: ${ext}. Allowed: ${ALLOWED_EXTENSIONS.join(', ')}`)
    }

    // Validate size
    if (file.size > MAX_FILE_SIZE) {
      throw new Error(`File too large (${(file.size / 1024 / 1024).toFixed(1)}MB). Maximum: 50MB`)
    }

    // Determine MIME type from extension (more reliable than file.type)
    const mimeType = getMimeType(file.name)
    if (!mimeType) {
      throw new Error(`Could not determine MIME type for ${file.name}`)
    }

    // Generate a unique document ID for the storage path
    const docId = uuidv4()
    const safeFileName = sanitizeStorageFileName(file.name)
    const storagePath = `${userId}/${mindmapId}/${docId}/${safeFileName}`

    // Upload to Supabase storage (RLS-protected bucket)
    const { error: uploadError } = await supabase.storage
      .from('venture-documents')
      .upload(storagePath, file, {
        upsert: false,
      })

    if (uploadError) {
      throw new Error(`Upload failed: ${uploadError.message}`)
    }

    // Trigger backend processing
    const headers = await TokenManager.getAuthHeaders()
    const response = await HttpClient.post<{ documentId: string }>(
      '/documents/process',
      {
        ventureNodeId,
        mindmapId,
        storagePath,
        fileName: file.name,
        mimeType,
        fileSize: file.size,
        ...(contentClassification ? { contentClassification } : {}),
      },
      headers
    )

    if (response.error) {
      // Clean up the uploaded file since processing won't happen
      await supabase.storage.from('venture-documents').remove([storagePath])
      throw new Error(response.error.message || 'Failed to process document')
    }

    return { documentId: response.data!.documentId }
  }

  /**
   * List all documents for a venture node.
   */
  static async listDocuments(mindmapId: string, ventureNodeId: string): Promise<VentureDocument[]> {
    const headers = await TokenManager.getAuthHeaders()
    const response = await HttpClient.get<{ documents: VentureDocument[] }>(
      `/documents/${mindmapId}/${ventureNodeId}`,
      headers
    )

    if (response.error) {
      throw new Error(response.error.message || 'Failed to list documents')
    }

    return response.data?.documents ?? []
  }

  /**
   * List all documents for a mindmap (used by the Research page).
   */
  static async listDocumentsForMindmap(mindmapId: string): Promise<VentureDocument[]> {
    const headers = await TokenManager.getAuthHeaders()
    const response = await HttpClient.get<{ documents: VentureDocument[] }>(
      `/documents/mindmap/${mindmapId}`,
      headers
    )

    if (response.error) {
      throw new Error(response.error.message || 'Failed to list documents')
    }

    return response.data?.documents ?? []
  }

  /**
   * Delete a document.
   */
  static async deleteDocument(documentId: string): Promise<void> {
    const headers = await TokenManager.getAuthHeaders()
    const response = await HttpClient.delete(`/documents/${documentId}`, headers)

    if (response.error) {
      throw new Error(response.error.message || 'Failed to delete document')
    }
  }
}
